import { lazy, Suspense, useState } from "react";
import { Routes, Route } from "react-router-dom";

import Loader from "components/common/loader";
import Sidebar from "layout/sidebar/sidebar";

import { ProtectedRoute } from "./protectedRoute";
import UnauthLayout from "layout/unauth-layout/unauthLayout";
import { useAuth } from "@clerk/clerk-react";
import Threads from "components/messaging-thread/threads";
import PlatformSettings from "components/platform-settings/platformSettings";
import Organization from "components/organization/organization";
import PaymentOverDueForMember from "components/billing/paymentOverdueForMember";

const Search = lazy(() => import("screens/Search"));
const Billing = lazy(() => import("components/billing/billing"));
const CollectionDetails = lazy(
  () => import("components/collection/collection-details/collectionDetails")
);
const CompanyInsights = lazy(
  () => import("components/collection/company-insights/companyInsights")
);
const AIDataScreen = lazy(() => import("screens/AIData"));
const RecentSearch = lazy(
  () => import("components/recent-searches/recentSearches")
);
const CollectionList = lazy(
  () => import("components/collection/collection-list/collectionList")
);
const CampaignLanding = lazy(
  () => import("components/campaign/campaign-landing/campaignLanding")
);
const CampaignDetails = lazy(
  () => import("components/campaign/campaign-details/campaignDetails")
);
const CampaignList = lazy(
  () => import("components/campaign/campaign-list/campaignList")
);
const CampaignLayout = lazy(
  () => import("components/campaign/campaign-layout/campaignLayout")
);
const DealTracker = lazy(() => import("components/deal/dealTracker"));
const Admin = lazy(() => import("components/admin/admin"));

interface AppRouterProps {
  loggedIn: boolean;
  userData: any;
  setUserData: (user: any) => void;
  credits: any;
  updateCredits: (credits: any) => void;
  saveToast: (text: string) => void;
}

export function AppRouter({
  loggedIn,
  userData,
  setUserData,
  credits,
  updateCredits,
  saveToast,
}: AppRouterProps) {
  const { isSignedIn } = useAuth();
  const [openNotifications, setOpenNotifications] = useState(false);

  return (
    <>
      {!isSignedIn && <UnauthLayout />}
      <div className="flex h-screen relative">
        <div className="flex-shrink-0 flex flex-row">
          {isSignedIn && userData && (
            <Sidebar
              loggedIn={loggedIn}
              user={userData}
              openNotifications={openNotifications}
              setOpenNotifications={setOpenNotifications}
            />
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          <Suspense fallback={<Loader />}>
            <div className="p-6 h-full">
              <Routes>
                <Route
                  path="/"
                  element={
                    <Search
                      user={userData}
                      creditsP={credits}
                      updateCredits={updateCredits}
                      saveToast={saveToast}
                    />
                  }
                />

                <Route
                  path="/search"
                  element={
                    <ProtectedRoute>
                      <Search
                        user={userData}
                        creditsP={credits}
                        updateCredits={updateCredits}
                        saveToast={saveToast}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/search/newUser"
                  element={
                    <Search
                      user={userData}
                      creditsP={credits}
                      updateCredits={updateCredits}
                      saveToast={saveToast}
                    />
                  }
                />

                <Route
                  path="/billing"
                  element={
                    <ProtectedRoute>
                      <Billing userP={userData} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/team-management"
                  element={
                    <ProtectedRoute>
                      <Organization />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/collectionDetail/:collectionId"
                  element={
                    <ProtectedRoute>
                      <CollectionDetails />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/company-insights/:companyNumber"
                  element={
                    <ProtectedRoute>
                      <CompanyInsights />
                    </ProtectedRoute>
                  }
                />

                <Route path="/aidata" element={<AIDataScreen />} />

                <Route
                  path="/recentSearches"
                  element={
                    <ProtectedRoute>
                      <RecentSearch />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/collections"
                  element={
                    <ProtectedRoute>
                      <CollectionList />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/campaignLanding"
                  element={
                    <ProtectedRoute>
                      <CampaignLanding />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/campaignDetails/:campaignId"
                  element={
                    <ProtectedRoute>
                      <CampaignDetails />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/campaign"
                  element={
                    <ProtectedRoute>
                      <CampaignList />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/campaignFlow"
                  element={
                    <ProtectedRoute>
                      <CampaignLayout />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/deals/:dealId?"
                  element={
                    <ProtectedRoute>
                      <DealTracker />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/threads"
                  element={
                    <ProtectedRoute>
                      <Threads />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/threads/:threadId"
                  element={
                    <ProtectedRoute>
                      <Threads />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/platform-settings"
                  element={
                    <ProtectedRoute>
                      <PlatformSettings />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/payment-overdue-member"
                  element={
                    <ProtectedRoute>
                      <PaymentOverDueForMember />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <Admin />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </div>
          </Suspense>
        </div>
      </div>
    </>
  );
}
